import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";

import { BTAvatar, btAvatarColorize, BTStatusBadge } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes } from "types/enum";

import { showAPIErrorMessage } from "utilities/apiHandler";

import { ApprovalStatus } from "commonComponents/approvals/ApprovalViewer/ApprovalViewer.types";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { BTUserInfoDisplay } from "commonComponents/btWrappers/BTUser/BTUserInfoDisplay";
import { BTEditorChat } from "commonComponents/btWrappers/editor/BTEditorChat/BTEditorChat";
import { CommentHeader } from "commonComponents/btWrappers/editor/BTEditorChat/BTEditorChatInterface";
import { type DiscussionCommentEntity } from "commonComponents/entity/comment/CommentContainer/CommentContainer.api.types";
import SelectedCommentMenu from "commonComponents/entity/comment/SelectedCommentMenu";
import { Reactions } from "commonComponents/entity/reaction/Reactions";

import {
    type UserInteractionsContract,
    UserInteractionsEntityType,
    UserInteractionsType,
} from "handlers";

import { useChatEditorConfig } from "entity/chat/common/useChatEditorConfig";
import { UserActivationPageHandler } from "entity/LinkAccountActivation/UserActivationPage.api.handler";
import { type UserProfileResponse } from "entity/login/Login/Login.api.types";

import "./CommentPresentational.less";

export interface ICommentProps {
    comment: DiscussionCommentEntity;
    onView?: () => void;
    onDeleteComment: (comment: DiscussionCommentEntity) => void;
    showCommentInfo: boolean;
    handler?: UserActivationPageHandler;
    onReplyToComment: () => void;
    isCommentSelected: boolean;
    reactionData: UserInteractionsContract[];
    onCommentReaction: (commentId: number, data: UserInteractionsContract) => Promise<void>;
    isExternal?: boolean;
    isReadOnly?: boolean;
}

/**
 * Maps a comment's persisted approval status to a badge. Only comments entered as part of an
 * approve/reject action carry Approved/Rejected; everything else shows no badge.
 */
function getApprovalStatusBadge(approvalStatus: ApprovalStatus | undefined) {
    switch (approvalStatus) {
        case ApprovalStatus.Approved:
            return { displayType: "success" as const, text: "Approved" };
        case ApprovalStatus.Rejected:
            return { displayType: "danger" as const, text: "Rejected" };
        default:
            return null;
    }
}

const readCommentDurationInMilliseconds = moment.duration(0.5, "seconds").asMilliseconds();

const defaultUserHandler = new UserActivationPageHandler();

const CommentPresentationalInternal = ({
    comment,
    onView,
    handler = defaultUserHandler,
    onDeleteComment,
    showCommentInfo,
    onReplyToComment,
    isCommentSelected,
    reactionData,
    onCommentReaction,
    isExternal,
}: ICommentProps) => {
    const [userProfile, setUserProfile] = useState<UserProfileResponse | undefined>(undefined);
    const userInfo = useRequiredContext(UserInfoContext);

    const anchorRef = useRef<HTMLDivElement>(null);

    const editorConfig = useChatEditorConfig();

    const viewed = useRef(false);

    const [inViewRef, inView] = useInView({
        delay: readCommentDurationInMilliseconds,
        threshold: 0.1,
    });

    useEffect(() => {
        if (inView && !viewed.current) {
            onView?.();
            viewed.current = true;
        }
    }, [inView, onView]);

    const handleLoadUserProfile = useCallback(async () => {
        try {
            const userProfile = await handler.profile(comment.addedById);
            setUserProfile(userProfile);
        } catch (e) {
            showAPIErrorMessage(e);
        }
    }, [comment.addedById, handler]);

    const handleVisibleChange = useCallback(
        (visible: boolean) => {
            if (visible && userProfile === undefined) {
                void handleLoadUserProfile();
            }
        },
        [handleLoadUserProfile, userProfile],
    );

    const commentName = comment.userFullName ?? comment.builderName ?? "";
    const approvalStatusBadge = getApprovalStatusBadge(comment.approvalStatus);
    const handleGetUserContent = useCallback(() => {
        return (
            <BTUserInfoDisplay
                showManageUser
                {...{
                    globalUserId: comment.addedById,
                    displayName: commentName,
                    userProfile,
                }}
            />
        );
    }, [commentName, comment.addedById, userProfile]);

    return (
        <div
            className={cn("bt-flex-row", "Comment gap-2 HasInvertedLayout", {
                CurrentUserComment: comment.byCurrentUser,
            })}
            key={comment.id}
            id={`comment-${comment.id}`}
        >
            <BTPopover
                onVisibleChange={handleVisibleChange}
                content={handleGetUserContent}
                disabled={userInfo.loginType !== BTLoginTypes.BUILDER}
                placement="right"
                widthType="unrestricted"
                trigger="click"
            >
                <span className="bds-h-8">
                    <BTAvatar
                        testid={`comment-avatar-${comment.addedById}`}
                        colorize={
                            btAvatarColorize[(comment.addedById ?? 0) % btAvatarColorize.length]
                        }
                        text={commentName}
                        size="sm"
                    />
                </span>
            </BTPopover>

            <div
                data-testid={`comment-${comment.id}`}
                className={cn("CommentCard bds-col bds-gap-2", {
                    "selected-comment": isCommentSelected,
                    HasReactions: reactionData.some((r) => moment(r.reactDateUtc).isValid()),
                })}
                tabIndex={0}
            >
                <span ref={inViewRef}>
                    <CommentHeader commentAuthor={commentName} commentDate={comment.commentDate} />
                </span>
                {approvalStatusBadge && (
                    <BTStatusBadge
                        testid={`comment-approval-status-${comment.id}`}
                        text={approvalStatusBadge.text}
                        displayType={approvalStatusBadge.displayType}
                        textOnly={true}
                    />
                )}
                {comment.commentJson.content ? (
                    <BTEditorChat
                        onBlur={() => {}}
                        onChange={() => {}}
                        id={`${comment.id}-content`}
                        data-testid={`${comment.id}-content`}
                        value={comment.commentJson}
                        readOnly
                        disableSubmitOnEnterKey
                        onSendChat={() => {}}
                        extensionConfig={editorConfig}
                    />
                ) : (
                    comment.commentText
                )}
                <Reactions
                    builderId={comment.builderId}
                    entityId={comment.id}
                    entityType={UserInteractionsEntityType.Comments}
                    data={reactionData}
                    onlyShowTypes={Object.values(UserInteractionsType).filter((t) =>
                        reactionData.some(
                            (r) => r.interactionType === t && moment(r.reactDateUtc).isValid(),
                        ),
                    )}
                    onReact={async (data) => await onCommentReaction(comment.id, data)}
                    buttonType="secondary"
                    showNames={userInfo.loginType === BTLoginTypes.BUILDER}
                    isReadOnly={isExternal}
                    className="ReactionsDisplay"
                />
                <div
                    ref={anchorRef}
                    style={{ position: "absolute", top: -40, right: 0 }}
                    className="FloatAnchor"
                />
                {isCommentSelected && (
                    <SelectedCommentMenu
                        comment={comment}
                        onDeleteComment={onDeleteComment}
                        showCommentInfo={showCommentInfo}
                        isListItem={false}
                        onReplyToComment={onReplyToComment}
                        reactionData={reactionData}
                        onCommentReaction={onCommentReaction}
                        isExternal={isExternal}
                        anchorRef={anchorRef}
                    />
                )}
            </div>
        </div>
    );
};

export const CommentPresentational = CommentPresentationalInternal;
